import { ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';

import { useAuthContext } from '../hooks';

type PermissionGuardProps = {
  requiredPermissions: string[];
  requireAll?: boolean;
  children: ReactNode;
};

/**
 * Permission guard that redirects to 403 if user doesn't have required permissions
 * @param requiredPermissions - Array of permission names to check
 * @param requireAll - If true, user must have all permissions. If false, user needs at least one (default: false)
 * @param children - Content to render if user has permission
 */
export default function PermissionGuard({
  requiredPermissions,
  requireAll = false,
  children,
}: PermissionGuardProps) {
  const { permissions } = useAuthContext();

  const hasPermission = requireAll
    ? requiredPermissions.every((permission) => permissions?.includes(permission))
    : requiredPermissions.some((permission) => permissions?.includes(permission));

  return hasPermission ? children : <Navigate to="/403" replace />;
}
